/**
 * Leave package catalog + policy rules (mirrors backend lv_default_seed).
 */

/** Not real leave balances — attendance / duration / admin-only. */
export const NON_LEAVE_BALANCE_CODES = ["HD", "WFH", "LOP", "CO", "FL"];

/** Admin-only — never in Basic or Advanced auto packages. */
export const ADMIN_OPTIONAL_CODES = ["HD", "WFH", "AL", "RL", "FL"];

/** All leave types an admin may add (Custom setup or Leave Setup). */
export const ALL_LEAVE_TYPES = [
  { code: "CL", name: "Casual Leave", color: "#3b82f6", is_paid: true, sort_order: 1 },
  { code: "SL", name: "Sick Leave", color: "#ef4444", is_paid: true, sort_order: 2 },
  { code: "EL", name: "Earned Leave", color: "#10b981", is_paid: true, sort_order: 3 },
  { code: "PL", name: "Privilege Leave", color: "#8b5cf6", is_paid: true, sort_order: 4 },
  { code: "LOP", name: "Loss Of Pay", color: "#6b7280", is_paid: false, sort_order: 5 },
  { code: "ML", name: "Maternity Leave", color: "#ec4899", is_paid: true, sort_order: 6, gender_restriction: "female" },
  { code: "PTL", name: "Paternity Leave", color: "#6366f1", is_paid: true, sort_order: 7, gender_restriction: "male" },
  { code: "BL", name: "Bereavement Leave", color: "#374151", is_paid: true, sort_order: 8 },
  { code: "MRL", name: "Marriage Leave", color: "#f472b6", is_paid: true, sort_order: 9 },
  { code: "CO", name: "Comp Off", color: "#06b6d4", is_paid: true, sort_order: 10 },
  { code: "OH", name: "Optional Holiday", color: "#84cc16", is_paid: true, sort_order: 11 },
  { code: "EML", name: "Emergency Leave", color: "#dc2626", is_paid: true, sort_order: 12 },
  { code: "STL", name: "Study Leave", color: "#64748b", is_paid: true, sort_order: 13 },
  { code: "HD", name: "Half Day", color: "#f59e0b", is_paid: true, sort_order: 14, admin_only: true },
  { code: "WFH", name: "Work From Home", color: "#0ea5e9", is_paid: true, sort_order: 15, admin_only: true },
  { code: "AL", name: "Academic Leave", color: "#14b8a6", is_paid: true, sort_order: 16, admin_only: true },
  { code: "RL", name: "Research Leave", color: "#a855f7", is_paid: true, sort_order: 17, admin_only: true },
  { code: "FL", name: "Festival Leave", color: "#eab308", is_paid: true, sort_order: 18, admin_only: true },
];

export const DEFAULT_POLICY_RULES = {
  // CL/SL: annual grant on plan allocate (not monthly accrual)
  CL: {
    yearly_limit: 12,
    monthly_limit: null,
    half_day: true,
    max_consecutive: 5,
    convert_insufficient_to_lop: true,
  },
  SL: {
    yearly_limit: 12,
    half_day: true,
    document_required_after_days: 2,
    convert_insufficient_to_lop: true,
  },
  // EL: monthly accrual; YTD credits are backfilled when the plan is applied
  EL: {
    yearly_limit: 15,
    half_day: true,
    accrual: { enabled: true, rate_per_month: 1.25, opening_balance: 0 },
    carry_forward: { enabled: true, max_days: 30 },
    min_service_months: 12,
  },
  PL: {
    yearly_limit: 15,
    half_day: true,
    accrual: { enabled: true, rate_per_month: 1.25, opening_balance: 0 },
    carry_forward: { enabled: true, max_days: 30 },
  },
  LOP: { yearly_limit: null, convert_insufficient_to_lop: true },
  ML: { yearly_limit: 182, gender: "female" },
  PTL: { yearly_limit: 15, gender: "male" },
  BL: { yearly_limit: 5, half_day: true },
  MRL: { yearly_limit: 5, half_day: false },
  CO: { yearly_limit: null, half_day: false },
  OH: { yearly_limit: 2, half_day: false },
  EML: { yearly_limit: 3, half_day: true },
  STL: { yearly_limit: null },
  HD: { yearly_limit: null },
  WFH: { yearly_limit: null },
  AL: { yearly_limit: null },
  RL: { yearly_limit: null },
  FL: { yearly_limit: null },
};

/** Standard company defaults — CL, SL, EL (not PL), LOP, statutory ML/PTL. */
export const BASIC_PACKAGE_CODES = ["CL", "SL", "EL", "LOP", "ML", "PTL"];

/** Basic + optional HR extras (correct day limits, no PL/HD/WFH/AL/RL/FL). */
export const ADVANCED_PACKAGE_CODES = [
  ...BASIC_PACKAGE_CODES,
  "BL",
  "MRL",
  "CO",
  "OH",
  "EML",
];

export const BASIC_PACKAGE_ITEMS = [
  "Casual Leave (12 days)",
  "Sick Leave (12 days)",
  "Earned Leave (15 days)",
  "Loss Of Pay",
  "Maternity Leave (statutory)",
  "Paternity Leave (15 days)",
];

export const ADVANCED_EXTRA_ITEMS = [
  "Bereavement Leave (5 days)",
  "Marriage Leave (5 days)",
  "Compensatory Off (earned)",
  "Optional Holiday (2 days)",
  "Emergency Leave (3 days)",
];

export function policyRulesForCode(code) {
  return DEFAULT_POLICY_RULES[code] ?? { yearly_limit: null, half_day: true };
}

export function isLeaveBalanceType(code) {
  return code && !NON_LEAVE_BALANCE_CODES.includes(String(code).toUpperCase());
}

export function getLeaveTypeByCode(code) {
  return ALL_LEAVE_TYPES.find((t) => t.code === code) || null;
}

export function getLeaveTypesForCodes(codes) {
  const set = new Set(codes);
  return ALL_LEAVE_TYPES.filter((t) => set.has(t.code));
}

/** Leave types shown when applying leave (excludes HD, WFH, etc.). */
export function filterApplicableLeaveTypes(types) {
  return (types || []).filter((t) => isLeaveBalanceType(t.code));
}
